import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// --- Public Pages ---
import Login from './pages/Login';
import Registration from './pages/Registration';

// --- Dashboard Layout & Core Pages ---
import DashboardLayout from './dashboard/Layout';
import Profile from './dashboard/user/profile/Profile';
import KYC from './dashboard/user/kyc/KYC';
import Payments from './dashboard/user/payments/Payments';
import Installments from './dashboard/user/installments/Installments';
import MyCustomers from './dashboard/user/my_customers/MyCustomers';
import UserDashboard from './dashboard/user/userdashboard/UserDashboard'; // 🌟 The new User Dashboard
import { ThemeProvider } from './context/ThemeContext';
import AlertModal from './reusable/AlertModal';
import MyCoupons from './dashboard/user/my_coupons/MyCoupons';

// --- Admin Pages ---
import AdminProfile from './dashboard/admin/admin_profile/AdminProfile';
import All_Customers from './dashboard/admin/all_customers/All_Customers';
import AllPayments from './dashboard/admin/all_payments/All_Payments';
import AllKYC from './dashboard/admin/all_kyc/All_Kyc';
import AllBankDetails from './dashboard/admin/all_bank_details/All_Bank_Details';
import AdminDashboard from './dashboard/admin/admindashboard/AdminDashboard';

// 🚨 Add your imports for these remaining pages here! 🚨
// import NetworkTree from './path/to/NetworkTree';
// import PayoutWallet from './path/to/PayoutWallet';
// import AdminDashboardHome from './path/to/AdminDashboardHome';
// import AdminApprovals from './path/to/AdminApprovals';


// --- 404 Fallback Component ---
const NotFound = () => (
  <div className="flex items-center justify-center h-screen bg-gray-50">
    <h1 className="text-2xl font-bold text-gray-500">404 - Page Not Found</h1>
  </div>
);


// ============================================================================
// ── ENTERPRISE SECURITY GUARDS ──────────────────────────────────────────────
// ============================================================================

// ============================================================================
// ── ENTERPRISE SECURITY GUARDS (Inside App.jsx) ─────────────────────────────
// ============================================================================

const getUserRole = () => {
  try {
    const token = localStorage.getItem('capstonne_access_token');
    if (!token || token === "null" || token === "undefined") return null;

    // 1. Get the payload part of the JWT
    const base64Url = token.split('.')[1];
    
    // 2. Convert Base64URL to standard Base64 (Crucial for JWTs!)
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    
    // 3. Decode securely
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join('')
    );

    const payload = JSON.parse(jsonPayload);
    
    // 4. Look for 'privilege' (which your backend uses) AND 'role'
    const role = payload.privilege || payload.role || payload.user_type || 'user';
    
    return role.toLowerCase();
  } catch (error) {
    console.error("Token decoding failed in Router:", error);
    return null; // Forces a logout if the token is completely broken
  }
};
// 1. Prevents logged-in users from seeing the Login/Register pages
const PublicRoute = ({ children }) => {
  const role = getUserRole();
  //if (role === 'admin') return <Navigate to="/dashboard/admin" replace />;
  //if (role === 'user') return <Navigate to="/dashboard/userdashboard" replace />;
  return children;
};

// 2. Protects the main layout wrapper (Requires ANY valid login)
const ProtectedLayout = ({ children }) => {
  const role = getUserRole();
  if (!role) return <Navigate to="/login" replace />;
  return children;
};

// 3. STRICT USER GUARD: Admins trying to access user pages are kicked to admin dashboard
const UserRoute = ({ children }) => {
  const role = getUserRole();
  if (role === 'admin') return <Navigate to="/dashboard/admin" replace />;
  return children;
};

// 4. STRICT ADMIN GUARD: Users trying to access admin pages are kicked to user dashboard
const AdminRoute = ({ children }) => {
  const role = getUserRole();
  if (role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

const DashboardIndex = () => {
  const role = getUserRole();
  
  if (role === 'admin') {
    return <Navigate to="/dashboard/admin" replace />;
  }
  
  // Standard users get routed here
  return <Navigate to="/dashboard/userdashboard" replace />; 
};
// Add this if you haven't created the real file yet

// ============================================================================
// ── MAIN APP COMPONENT ──────────────────────────────────────────────────────
// ============================================================================
const App = () => {
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [isForbidden, setIsForbidden] = useState(false); 

  useEffect(() => {
    const handleExpired = () => setIsSessionExpired(true);
    const handleForbidden = () => setIsForbidden(true); 

    window.addEventListener("capstonne_session_expired", handleExpired);
    window.addEventListener("capstonne_account_forbidden", handleForbidden);
    
    return () => {
      window.removeEventListener("capstonne_session_expired", handleExpired);
      window.removeEventListener("capstonne_account_forbidden", handleForbidden);
    };
  }, []);

  const handleSessionClose = () => {
    setIsSessionExpired(false);
    setIsForbidden(false);
    localStorage.removeItem('capstonne_access_token');
    localStorage.removeItem('capstonne_refresh_token');
    window.location.href = '/login'; 
  };

  return (
    <ThemeProvider>
      <AlertModal 
        isOpen={isSessionExpired}
        onClose={handleSessionClose}
        title="Session Expired"
        message="Your security token has expired. You must re-authenticate to access the Capstonne dashboard."
      />
      <AlertModal 
        isOpen={isForbidden}
        onClose={handleSessionClose}
        title="Account Pending"
        message="Your account is under review or requires verification. You cannot access this data until an admin approves your profile."
      />

      <Router>
        <Routes>
          {/* --- PUBLIC ROUTES --- */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register/:refCode?" element={<PublicRoute><Registration /></PublicRoute>} />

          {/* --- SECURE DASHBOARD (ProtectedLayout ensures they are logged in) --- */}
          <Route path="/dashboard" element={<ProtectedLayout><DashboardLayout /></ProtectedLayout>}>
            
            {/* The Traffic Cop intercepts the blank "/dashboard" path */}
            <Route index element={<DashboardIndex />} />

            {/* STRICT USER ROUTES */}
            <Route path="userdashboard" element={<UserRoute><UserDashboard/></UserRoute>} />
            <Route path="my_customers" element={<UserRoute><MyCustomers /></UserRoute>} /> 
            <Route path="profile" element={<UserRoute><Profile /></UserRoute>} /> 
            <Route path="kyc" element={<UserRoute><KYC /></UserRoute>} /> 
            <Route path="payments" element={<UserRoute><Payments/></UserRoute>} />
            <Route path="installments" element={<UserRoute><Installments/></UserRoute>} />
            <Route path="my_coupons" element={<UserRoute><MyCoupons/></UserRoute>} />


            {/* STRICT ADMIN ROUTES */}
            <Route path="admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} /> {/*} Make sure AdminDashboardHome is imported */}
            <Route path="admin/all_customers" element={<AdminRoute><All_Customers /></AdminRoute>} />
            <Route path="admin/profile" element={<AdminRoute><AdminProfile/></AdminRoute>} />
            <Route path="admin/all_payments" element={<AdminRoute><AllPayments/></AdminRoute>} />
            <Route path="admin/all_kyc" element={<AdminRoute><AllKYC/></AdminRoute>} />
            <Route path="admin/all_bank_details" element={<AdminRoute><AllBankDetails/></AdminRoute>} />

          </Route>

          {/* --- FALLBACK --- */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default App;